import {Injectable} from '@angular/core';
import {SessionService} from './session.service';
import {UserContextModel} from '../models/post-message/contents/userContext.model';
import {ViewContextEnum} from '../enums/view-context.enum';
import {PostMessageModel} from '../models/post-message/post-message.model';
import {PostMessageTypeEnum} from '../enums/post-message-send-type.enum';
import {CaseContextModel} from '../models/post-message/contents/caseContext.model';
import {TenantInformationService} from './tenant-information.service';
import {DocumentHal} from '../models/HAL/document-hal.model';

@Injectable({
  providedIn: 'root'
})
export class PostMessageService {

  constructor(
    private readonly sessionService: SessionService,
    private readonly tenantInformationService: TenantInformationService,
  ) {
  }

  /**
   * Creates a postMessage model for the user context.
   * @param viewContext - The viewContext, from where the Adaptive Form is opened.
   */
  public getUserContextPostMessageModel(viewContext: ViewContextEnum): PostMessageModel {
    const currentUser = this.sessionService.getSession().user;
    if (currentUser) {
      const userContext: UserContextModel = {
        identification: currentUser.identification,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
        viewContext: viewContext
      };
      return {
        type: PostMessageTypeEnum.USER_CONTEXT,
        version: "1.0.0",
        content: userContext,
        tenant: this.tenantInformationService.getTenantName()
      };
    }
    throw new Error("User not found in session");
  }

  /**
   * Creates a postMessage model for the data capturing process.
   * @param document - The document for which the data capturing is performed.
   */
  public getDataCapturingPostMessageModel(document: DocumentHal): PostMessageModel {
    const caseContext: CaseContextModel = {
      caseId: document.caseId,
      documentId: document.identification
    };
    return {
      type: PostMessageTypeEnum.DATA_CAPTURING,
      version: "1.0.0",
      content: caseContext,
      tenant: this.tenantInformationService.getTenantName()
    };
  }

  /**
   * Creates a postMessage model for the save progress process.
   * @param document - The document for which the save progress is performed.
   */
  public getSaveProgressPostMessageModel(document: DocumentHal): PostMessageModel {
    const caseContext: CaseContextModel = {
      caseId: document.caseId,
      documentId: document.identification
    };
    return {
      type: PostMessageTypeEnum.SAVE_PROGRESS,
      version: "1.0.0",
      content: caseContext,
      tenant: this.tenantInformationService.getTenantName()
    };
  }

  /**
   * Creates a postMessage model for the render preview process.
   * @param document - The document for which the render preview is performed.
   */
  public getRenderPreviewPostMessageModel(document: DocumentHal): PostMessageModel {
    const caseContext: CaseContextModel = {
      caseId: document.caseId,
      documentId: document.identification
    };
    return {
      type: PostMessageTypeEnum.RENDER_PREVIEW,
      version: "1.0.0",
      content: caseContext,
      tenant: this.tenantInformationService.getTenantName()
    };
  }

  /**
   * Sends the provided postMessage to the given iFrame.
   * @param iframeTarget - The iFrame to which the postMessage should be sent.
   * @param postMessage - The postMessage to be sent.
   */
  public postMessage(iframeTarget: HTMLIFrameElement, postMessage: PostMessageModel) {
    iframeTarget?.contentWindow?.postMessage(postMessage, iframeTarget.src);
  }
}
